import { useState } from 'react';
import { Filter, SearchInput, SelectInput, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Avatar, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Business } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { VENDOR_STATUS, STATUS_COLORS } from './constants';

interface VendorItem {
  id: string;
  status: string;
  vendorName: string;
  registrationNumber?: string;
  legalEntity?: {
    companyName?: string;
    email?: string;
    phone?: string;
  };
}

export const TESVendorList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: vendors, isLoading } = useGetList('tes-vendors', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const stats = {
    total: vendors?.length ?? 0,
    active: vendors?.filter((v: VendorItem) => v.status === 'Active').length ?? 0,
    suspended: vendors?.filter((v: VendorItem) => v.status === 'Suspended').length ?? 0,
    blacklisted: vendors?.filter((v: VendorItem) => v.status === 'Blacklisted').length ?? 0,
    pendingApproval: vendors?.filter((v: VendorItem) => v.status === 'Pending Approval').length ?? 0,
    totalWorkers: 0,
  };

  const statusCounts: Record<string, number> = vendors?.reduce((acc: Record<string, number>, v: VendorItem) => {
    acc[v.status] = (acc[v.status] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(statusCounts).map(([label, value]) => ({ label, value }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="TES Vendors"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Suspended', value: String(stats.suspended), color: 'warning.main' },
            { label: 'Blacklisted', value: String(stats.blacklisted), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
            {vendors?.map((vendor: VendorItem) => (
            <Paper key={vendor.id} sx={{ p: 2 }} onClick={() => navigate(`/tes-vendors/${vendor.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'info.main', fontSize: 14, color: 'white' }}>
                    <Business sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{vendor.vendorName}</Typography>
                    <Typography variant="caption" color="text.secondary">Reg: {vendor.registrationNumber}</Typography>
                  </Box>
                </Box>
                <Chip label={vendor.status} size="small" color={STATUS_COLORS[vendor.status] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">{vendor.legalEntity?.companyName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{vendor.legalEntity?.email || '—'}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="TES Vendors"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Suspended', value: String(stats.suspended), color: 'warning.main' },
          { label: 'Blacklisted', value: String(stats.blacklisted), color: 'error.main' },
          { label: 'Total Workers', value: String(stats.totalWorkers), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Vendor Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Registration #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {vendors?.map((vendor: VendorItem) => (
              <TableRow
                key={vendor.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tes-vendors/${vendor.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'info.main', fontSize: 12, color: 'white' }}>
                      <Business sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>{vendor.vendorName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{vendor.registrationNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{vendor.legalEntity?.companyName || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{vendor.legalEntity?.email || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={vendor.status} size="small" color={STATUS_COLORS[vendor.status] as ChipColor} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={vendors?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!vendors || vendors.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Business sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No vendors found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const TESVendorFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search vendor name" />
    <SelectInput source="status" choices={VENDOR_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);
